import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { observer } from 'mobx-react-lite';
import { action } from 'mobx';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';

import { Blueprint, Campaign, Policy } from '@vromo/models';
import { Site } from '@vromo/models';
import { useModals } from '@vromo/ui/components/Modals';

import Input, { InputLabel } from '@vromo/ui/components/Input';
import Flex from '@vromo/ui/components/Flex';
import Select from '@vromo/ui/components/Select2';
import Tooltip from '@vromo/ui/components/Tooltip';
import TelInput from '@vromo/dashboard/src/common/components/ControlledTelInput/ControlledTelInput';
import FontAwesome from '@vromo/ui/components/FontAwesome';

import sortBy from 'lodash/sortBy';
import AutodispatcherHeader from './AutodispatcherHeader';
import { qaLabelTestSites } from './../../../../../../common-qa-labels/sites';
import { useTranslation } from 'react-i18next';
import styles from './general.style';

const FilterRow = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px 0px;

    & > label {
        flex: 1;
    }

    input,
    select {
        margin-top: 5px;
    }
`;

type NumberOption = {
    label: string;
    value: number;
};

type Props = {
    editedSite: Site;
    siteData: Site;
    policyOptions: { label: string; value: number }[];
};

interface HandleSelectProps {
    selected: NumberOption;
    type: 'policy' | 'campaign' | 'blueprint';
    isHigherLevel: boolean;
    removeMessageKey: string;
    setIsHigherLevel: React.Dispatch<React.SetStateAction<boolean>>;
}

export default observer<Props>(function EditGeneral({ editedSite, policyOptions, siteData }) {
    const {
        viewStore: { callingCode, country },
        campaignStore,
        groupStore,
        blueprintStore
    } = useStore<RootStore>('rootStore');

    const campaignOptions = sortBy(
        campaignStore.campaigns.map((c) => ({ label: c.name, value: c.id })),
        ['label']
    );

    const groupOptions = sortBy(
        groupStore.groups.map((g) => ({ label: g.name, value: g.id })),
        ['label']
    );

    const blueprintOptions = sortBy(
        blueprintStore.blueprints.map((b: Blueprint) => ({ label: b.name, value: b.id })),
        ['label']
    );

    const modals = useModals();
    const { t } = useTranslation();

    const [autoDispatchEnabled, setAutoDispatchEnabled] = useState(!!editedSite.policy);

    const [isHigherLevelBlueprint, setIsHigherLevelBlueprint] = useState<boolean>(
        !blueprintOptions.some((bp) => bp?.value === editedSite.blueprint?.id) &&
            !!siteData.blueprint
    );

    const [isHigherLevelCampaign, setIsHigherLevelCampaign] = useState<boolean>(
        !campaignOptions.some((campaign) => campaign?.value === editedSite.campaign?.id) &&
            !!siteData.campaign
    );

    const [isHigherLevelPolicy, setIsHigherLevelPolicy] = useState<boolean>(
        !policyOptions.some((policy) => policy?.value === editedSite.policy?.id) &&
            !!siteData.policy
    );

    const toggleAutoDispatchEnabled = () =>
        setAutoDispatchEnabled((prevAutoDispatchEnabled) => !prevAutoDispatchEnabled);

    const handleSelect = async ({
        selected,
        type,
        isHigherLevel,
        removeMessageKey,
        setIsHigherLevel
    }: HandleSelectProps) => {
        if (!selected) {
            if (isHigherLevel) {
                if (await modals.confirm(t(removeMessageKey), t('editSiteModal.warningMessage'))) {
                    if (type === 'campaign') {
                        editedSite.campaign = null;
                    } else {
                        editedSite[type] = undefined;
                    }
                    setIsHigherLevel(false);
                }
            } else {
                if (type === 'campaign') {
                    editedSite.campaign = null;
                } else {
                    editedSite[type] = undefined;
                }
            }
            return;
        }

        if (isHigherLevel) {
            if (await modals.confirm(t(removeMessageKey), t('editSiteModal.warningMessage'))) {
                editedSite[type] = { id: selected?.value } as Policy & Campaign & Blueprint;
                setIsHigherLevel(false);
            }
        } else {
            editedSite[type] = { id: selected?.value } as Policy & Campaign & Blueprint;
        }
    };

    useEffect(() => {
        if (!autoDispatchEnabled) {
            editedSite.policy = null;
        } else {
            if (policyOptions?.length && !editedSite.policy)
                editedSite.policy = { id: policyOptions[0]!.value } as Policy;
        }
    }, [autoDispatchEnabled]);

    return (
        <>
            <FilterRow>
                <label>
                {t('editSiteModal.nameLabel')}
                    <Input
                        data-testid={qaLabelTestSites.addSiteModal.nameInput}
                        value={editedSite.contact?.name ? editedSite.contact.name : ''}
                        onChange={action((e) => (editedSite.contact!.name = e.target.value))}
                    />
                </label>
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.phoneRow}>
                <InputLabel optional label="Phone Number">
                    <div style={styles.numberInput} />
                    <TelInput
                        value={editedSite.contact?.phone || ''}
                        default={callingCode}
                        defaultCountry={country}
                        onChange={action((number: string) => {
                            editedSite.contact!.phone = number.length ? number : null;
                        })}
                    />
                </InputLabel>
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.groupRow}>
                <label>{t('editSiteModal.groupLabel')}</label>
                <Select
                    value={
                        editedSite.group
                            ? groupOptions.find((g) => g.value === editedSite.group)
                            : null
                    }
                    onChange={action((selected: NumberOption) => {
                        editedSite.group = selected?.value;
                    })}
                    options={groupOptions}
                    placeholder="Select a Group..."
                />
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.autoDispatcherRow}>
                <AutodispatcherHeader
                    autoDispatchEnabled={autoDispatchEnabled}
                    hasPolicies={!!policyOptions.length}
                    showTooltip={isHigherLevelPolicy}
                    onChange={toggleAutoDispatchEnabled}
                />
                <Select
                    isDisabled={!autoDispatchEnabled}
                    value={
                        editedSite.policy
                            ? policyOptions.find((p) => p.value === editedSite.policy!.id) || {
                                  label: siteData?.policy?.name,
                                  value: siteData?.policy?.id
                              }
                            : null
                    }
                    onChange={(selected: NumberOption) => {
                        handleSelect({
                            selected,
                            type: 'policy',
                            isHigherLevel: isHigherLevelPolicy,
                            removeMessageKey: 'editSiteModal.removeAutoDispatcher',
                            setIsHigherLevel: setIsHigherLevelPolicy
                        });
                    }}
                    options={policyOptions}
                    placeholder={
                        autoDispatchEnabled
                            ? t('editSiteModal.selectAutoDispatcher')
                            : t('editSiteModal.manualDispatch')
                    }
                    isClearable
                />
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.campaignRow}>
                <Flex horizontal="left" vertical="middle">
                    <label>{t('editSiteModal.campaignLabel')}</label>
                    {isHigherLevelCampaign && (
                        <Tooltip label={t('editSiteModal.accountLevelTooltip')}>
                            <div style={styles.tooltip}>
                                <FontAwesome icon="info-circle" />
                            </div>
                        </Tooltip>
                    )}
                </Flex>
                <Select
                    value={
                        editedSite.campaign
                            ? campaignOptions.find((c) => c.value === editedSite.campaign!.id) || {
                                  label: siteData?.campaign?.name,
                                  value: siteData?.campaign?.id
                              }
                            : null
                    }
                    onChange={action((selected: NumberOption) => {
                        handleSelect({
                            selected,
                            type: 'campaign',
                            isHigherLevel: isHigherLevelCampaign,
                            removeMessageKey: 'editSiteModal.removeCampaign',
                            setIsHigherLevel: setIsHigherLevelCampaign
                        });
                    })}
                    options={campaignOptions}
                    placeholder={t('editSiteModal.selectCampaign')}
                    isClearable
                />
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.jobTemplateRow}>
                <Flex horizontal="left" vertical="middle">
                    <label>{t('editSiteModal.jobTemplateLabel')}</label>
                    {isHigherLevelBlueprint && (
                        <Tooltip label={t('editSiteModal.accountLevelTooltip')}>
                            <div style={styles.tooltip}>
                                <FontAwesome icon="info-circle" />
                            </div>
                        </Tooltip>
                    )}
                </Flex>
                <Select
                    value={
                        editedSite.blueprint?.id
                            ? blueprintOptions.find(
                                  (it) => it.value === editedSite.blueprint!.id
                              ) || {
                                  label: siteData?.blueprint?.name,
                                  value: siteData?.blueprint?.id
                              }
                            : null
                    }
                    onChange={action((selected: NumberOption) => {
                        handleSelect({
                            selected,
                            type: 'blueprint',
                            isHigherLevel: isHigherLevelBlueprint,
                            removeMessageKey: 'editSiteModal.removeBlueprint',
                            setIsHigherLevel: setIsHigherLevelBlueprint
                        });
                    })}
                    options={blueprintOptions}
                    placeholder={t('editSiteModal.selectBlueprint')}
                    isClearable
                />
            </FilterRow>
        </>
    );
});
